import type { HTMLAttributes } from 'react';
import { memo } from 'react';

import type { ReactTableCell, ReactTableState } from './types';

interface TableCellContentProps {
  // Narrowed to the one member read below, whose signature is row-type agnostic,
  // so a cell of any row type is assignable without widening to `any`.
  cell: Pick<ReactTableCell<object>, 'render'>;
  // eslint-disable-next-line react/no-unused-prop-types -- read by the memo comparator below, not by the render.
  isResizing: boolean;
}

/**
 * A cell's rendered content, held still for the duration of a resize drag: the
 * drag changes column widths only, so re-rendering every visible cell's content
 * each frame is wasted work on a table whose cells are expensive.
 */
const TableCellContent = memo(
  ({ cell }: TableCellContentProps) => <>{cell.render('Cell')}</>,
  // Equal, and so skipped, only between two frames of the same drag: a cell still
  // re-renders normally the moment one starts or ends. Data landing mid-drag shows
  // stale until release — comparing `cell` would fix that but skip nothing, since
  // v7 rebuilds every cell object each render.
  (previous, next) => previous.isResizing && next.isResizing,
);

TableCellContent.displayName = 'TableCellContent';

export interface TableCellProps<T extends object> {
  /**
   * The cell to render.
   */
  cell: ReactTableCell<T>;
  /**
   * Props derived from `getCellProps` (className, style, etc.), minus the key.
   */
  cellProps: HTMLAttributes<HTMLDivElement>;
  /**
   * Extension point for per-cell DOM props.
   */
  getTdProps: (
    state: ReactTableState<T>,
    cell: ReactTableCell<T>,
  ) => HTMLAttributes<HTMLTableCellElement>;
  /**
   * The current table state.
   */
  state: ReactTableState<T>;
}

/**
 * Renders a single body cell (the `rt-td` wrapper) and its content.
 *
 * @deprecated Internal to the legacy `ReactTable`; superseded by the table
 * primitives in `@components/table`.
 */
const TableCell = <T extends object>({
  cell,
  cellProps,
  getTdProps,
  state,
}: TableCellProps<T>) => (
  <div {...getTdProps(state, cell)} {...cellProps}>
    <TableCellContent
      cell={cell}
      isResizing={Boolean(state.columnResizing?.isResizingColumn)}
    />
  </div>
);

export default TableCell;
